// admin/src/features/preview/DeviceFrame.tsx
//
// A phone or watch at physical size. The case and screen are laid out in
// the device's own points, then the whole thing is scaled by
// mm-per-point x CSS-px-per-mm, so a 440 pt phone screen measures 72.9 mm
// on any monitor once calibrated. Its size never follows the window: a
// window smaller than the device scrolls instead of shrinking it.
import type { ReactNode } from 'react';
import { bodyPt, realSizeScale, type DeviceSpec } from './devices';

export function DeviceFrame({ device, cssPxPerMm, children }: { device: DeviceSpec; cssPxPerMm: number; children: ReactNode }) {
  const s = realSizeScale(device, cssPxPerMm);
  const body = bodyPt(device);
  const bezelX = (body.width - device.widthPt) / 2;
  const bezelY = (body.height - device.heightPt) / 2;
  const isWatch = device.kind === 'watch';
  const side = isWatch ? 16 : 5;
  const band = isWatch ? body.height * 0.3 : 0;
  const width = body.width + side * 2;
  const height = body.height + band * 2;

  return (
    <div style={{ width: width * s, height: height * s, flexShrink: 0 }}>
      <div style={{ position: 'relative', width, height, transform: `scale(${s})`, transformOrigin: '0 0' }}>
        {isWatch ? (
          <>
            <span className="pv-band" style={{ left: side + body.width * 0.13, width: body.width * 0.74, top: 0, height: band + 20, borderRadius: '14px 14px 0 0' }} />
            <span className="pv-band" style={{ left: side + body.width * 0.13, width: body.width * 0.74, bottom: 0, height: band + 20, borderRadius: '0 0 14px 14px' }} />
            <span className="pv-crown" style={{ left: side + body.width - 3, top: band + body.height * 0.24, width: 15, height: 34 }} />
            <span className="pv-side-button" style={{ left: side + body.width - 2, top: band + body.height * 0.56, width: 8, height: 48 }} />
          </>
        ) : (
          <>
            <span className="pv-side-button" style={{ left: 1, top: body.height * 0.17, width: 6, height: 30 }} />
            <span className="pv-side-button" style={{ left: 1, top: body.height * 0.25, width: 6, height: 56 }} />
            <span className="pv-side-button" style={{ left: 1, top: body.height * 0.33, width: 6, height: 56 }} />
            <span className="pv-side-button" style={{ left: side + body.width - 2, top: body.height * 0.27, width: 6, height: 92 }} />
          </>
        )}
        <div
          className={isWatch ? 'pv-case pv-case-watch' : 'pv-case'}
          style={{ left: side, top: band, width: body.width, height: body.height, borderRadius: device.cornerPt + Math.min(bezelX, bezelY) }}
        >
          <div className="pv-screen" style={{ left: bezelX, top: bezelY, width: device.widthPt, height: device.heightPt, borderRadius: device.cornerPt }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
